import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, retry, throwError, timer } from 'rxjs';
import { environment } from '../../../environments/environment.local';
import { ModeService } from '../mode/mode.service';
import type { RoleTerminology } from '../api/tenant.service';
import {
  TenantPublicService,
  type TenantPublicBranding,
  type TenantPublicDto,
  type TenantPublicStatus,
} from '../api/tenant-public.service';
import { ThemeService } from '../theme/theme.service';

export type TenantContextState =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'suspended'
  | 'pending'
  | 'not_found'
  | 'error';

export const ENGLISH_FALLBACK_TERMINOLOGY: RoleTerminology = {
  coachSingular: 'Coach',
  coachPlural: 'Coaches',
  athleteSingular: 'Athlete',
  athletePlural: 'Athletes',
  teamSingular: 'Team',
  teamPlural: 'Teams',
  groupSingular: 'Group',
  groupPlural: 'Groups',
  classSingular: 'Class',
  classPlural: 'Classes',
};

@Injectable({ providedIn: 'root' })
export class TenantContextService {
  private readonly publicApi = inject(TenantPublicService);
  private readonly mode = inject(ModeService);
  private readonly theme = inject(ThemeService);

  private readonly _state = signal<TenantContextState>('idle');
  private readonly _tenant = signal<TenantPublicDto | null>(null);

  readonly state = this._state.asReadonly();
  readonly tenant = this._tenant.asReadonly();

  readonly isReady = computed(() => this._state() === 'ready');
  readonly isMissing = computed(() => this._state() === 'not_found');
  readonly isBlocked = computed(
    () => this._state() === 'suspended' || this._state() === 'pending',
  );
  readonly isLoading = computed(
    () => this._state() === 'idle' || this._state() === 'loading',
  );
  readonly hasError = computed(() => this._state() === 'error');

  readonly teamName = computed(() => this._tenant()?.teamName ?? '');
  readonly discipline = computed(() => this._tenant()?.discipline ?? null);
  readonly logoUrl = computed(() => this._tenant()?.branding.logoUrl ?? null);
  readonly coverPhotoUrl = computed(
    () => this._tenant()?.branding.coverPhotoUrl ?? null,
  );
  readonly hideSportDeskMentions = computed(
    () => this._tenant()?.branding.hideSportDeskMentions ?? false,
  );
  readonly status = computed<TenantPublicStatus | null>(
    () => this._tenant()?.status ?? null,
  );
  readonly countryCode = computed(() => this._tenant()?.countryCode ?? null);
  readonly publicSiteEnabled = computed(
    () => this._tenant()?.publicSiteEnabled ?? false,
  );

  readonly terminology = computed<RoleTerminology>(() => {
    const dto = this._tenant();
    if (!dto) return ENGLISH_FALLBACK_TERMINOLOGY;
    const byLocale = dto.terminologyByLocale;
    const userLocale = typeof navigator !== 'undefined' ? navigator.language : dto.locale;
    return (
      byLocale[userLocale] ??
      byLocale[userLocale.split('-')[0]] ??
      byLocale[dto.locale] ??
      ENGLISH_FALLBACK_TERMINOLOGY
    );
  });

  load(): void {
    const subdomain = this.mode.subdomain();
    if (!subdomain) return;
    this._state.set('loading');
    retryOn5xx(this.publicApi.getBySubdomainOrId(subdomain)).subscribe({
      next: (dto) => this.apply(dto),
      error: (err) => {
        if (err instanceof HttpErrorResponse && err.status === 404) {
          this._tenant.set(null);
          this._state.set('not_found');
        } else {
          this._state.set('error');
        }
      },
    });
  }

  reload(): void {
    const subdomain = this.mode.subdomain();
    if (subdomain) this.publicApi.invalidate(subdomain);
    this.load();
  }

  private apply(dto: TenantPublicDto): void {
    this._tenant.set(dto);
    this._state.set(stateForStatus(dto.status));
    applyTheme(dto.branding);
    this.theme.setTenantPrimary(dto.branding.primaryColorHex || null);
    applyPwaManifest(dto);
    maybeRedirectToCanonical(dto);
  }
}

let currentManifestBlobUrl: string | null = null;

function applyPwaManifest(dto: TenantPublicDto): void {
  if (typeof document === 'undefined') return;
  const logo = dto.branding.logoUrl;
  const title = dto.teamName || 'SportDesk';
  const primary = dto.branding.primaryColorHex || '#2E6BFF';
  const logoMime = logo ? inferMime(logo) : 'image/png';

  const appleLink = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  if (appleLink) appleLink.href = logo ?? '/assets/favico/apple-touch-icon.png';

  // Favicon (browser tab / bookmarks) → tenant logo when present. The static
  // <link rel="icon"> tags advertise specific types (svg/png/ico), so the type
  // is updated too — otherwise a raster logo behind an svg-typed link won't render.
  if (logo) {
    document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]').forEach((link) => {
      link.href = logo;
      link.type = logoMime;
    });
  }

  const appleTitle = document.querySelector<HTMLMetaElement>(
    'meta[name="apple-mobile-web-app-title"]',
  );
  if (appleTitle) appleTitle.content = title;

  // theme-color meta is owned by ThemeService (so it can switch to a dark color in dark mode).
  // We still set theme_color inside the manifest below using the tenant primary.

  const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!manifestLink) return;

  const icons = logo
    ? [
        { src: logo, sizes: 'any', type: logoMime, purpose: 'any' },
        { src: logo, sizes: '192x192', type: logoMime, purpose: 'any maskable' },
        { src: logo, sizes: '512x512', type: logoMime, purpose: 'any maskable' },
      ]
    : [
        { src: '/assets/favico/favicon.svg', sizes: 'any', type: 'image/svg+xml', purpose: 'any' },
        {
          src: '/assets/favico/web-app-manifest-192x192.png',
          sizes: '192x192',
          type: 'image/png',
          purpose: 'any maskable',
        },
        {
          src: '/assets/favico/web-app-manifest-512x512.png',
          sizes: '512x512',
          type: 'image/png',
          purpose: 'any maskable',
        },
      ];

  const manifest = {
    name: title,
    short_name: title.length > 12 ? title.slice(0, 12) : title,
    description: `Tu faci sport, ${title} se ocupă de rest`,
    start_url: '/',
    scope: '/',
    display: 'standalone',
    orientation: 'portrait',
    // Launch/splash backdrop. Kept a brand-neutral dark (the app's own shell bg)
    // rather than white so it never flashes white, and never a near-white tenant
    // primary either. The tenant identity comes through name/icons/theme_color.
    background_color: '#0A1628',
    theme_color: primary,
    lang: 'ro',
    dir: 'ltr',
    icons,
  };

  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
  const url = URL.createObjectURL(blob);
  if (currentManifestBlobUrl) URL.revokeObjectURL(currentManifestBlobUrl);
  currentManifestBlobUrl = url;
  manifestLink.href = url;
}

function inferMime(url: string): string {
  const lower = url.toLowerCase();
  if (lower.endsWith('.svg')) return 'image/svg+xml';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  return 'image/png';
}

function retryOn5xx(source$: Observable<TenantPublicDto>): Observable<TenantPublicDto> {
  return source$.pipe(
    retry({
      count: 3,
      delay: (err, retryCount) => {
        if (err instanceof HttpErrorResponse && err.status >= 500) {
          return timer(500 * 2 ** (retryCount - 1));
        }
        return throwError(() => err);
      },
    }),
  );
}

function stateForStatus(status: TenantPublicStatus): TenantContextState {
  switch (status) {
    case 'Active':
      return 'ready';
    case 'Suspended':
      return 'suspended';
    case 'PendingEmailVerification':
      return 'pending';
  }
}

function applyTheme(branding: TenantPublicBranding): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const primary = branding.primaryColorHex;
  const accent = branding.accentColorHex ?? branding.primaryColorHex;
  root.style.setProperty('--tenant-primary', primary);
  root.style.setProperty('--tenant-accent', accent);
  root.style.setProperty('--tenant-on-primary', readableTextColor(primary));
}

function maybeRedirectToCanonical(dto: TenantPublicDto): void {
  if (!environment.production) return;
  if (typeof window === 'undefined') return;
  try {
    const canonical = new URL(dto.loginUrl);
    if (canonical.host && canonical.host !== window.location.host) {
      window.location.replace(dto.loginUrl);
    }
  } catch {
    // malformed loginUrl — ignore; don't break the app
  }
}

function readableTextColor(hex: string): string {
  const rgb = parseHex(hex);
  if (!rgb) return '#ffffff';
  const luminance = relativeLuminance(rgb);
  return luminance > 0.55 ? '#0A1628' : '#ffffff';
}

function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const h = m[1];
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const norm = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * norm(r) + 0.7152 * norm(g) + 0.0722 * norm(b);
}
